import { useEffect } from 'react';
import { Typography } from 'antd';
import { useAuth } from '@entities/session';
import type { CaptchaState } from '@entities/session';

/**
 * Общее для трёх страниц с капчей: регистрация, «Забыли пароль?», подтверждение адреса
 * (план `docs/smart-captcha-plan.md` §5 и §12).
 *
 * Одним местом, а не тремя копиями: правила здесь неочевидные и держатся на свойствах стороннего
 * скрипта, а не на удобстве — разъехавшись по страницам, они чинились бы «обратно» по одной.
 */

/**
 * Уход ко входу — **полной навигацией**, а не SPA-переходом.
 *
 * `captcha.js` Яндекса исполняется в origin портала и, однажды загруженный, остаётся в документе и
 * после ухода со страницы: `destroy()` снимает виджет, но не скрипт. Значит, документ со скриптом
 * обязан умереть до того, как человек наберёт логин и пароль, — иначе сторонний код присутствует
 * ровно в тот момент, когда в форму вводят учётные данные (§12).
 *
 * `navigate('/login')` и `<Link to="/login">` здесь неверны, хотя и выглядят «более правильными
 * для SPA»: они документ как раз сохраняют. По той же причине ссылки на вход на этих страницах —
 * обычные `<a href="/login">`, а не `Link`.
 */
export function goToLogin(): void {
  window.location.assign('/login');
}

/**
 * Гвард страницы с капчей для уже вошедшей вкладки: форму рисовать нельзя, надо уходить в портал.
 *
 * Возвращает `true`, когда страница обязана ничего не рендерить. Уход — полной навигацией, а не
 * `<Navigate>`: причина та же, что у `goToLogin` — при SPA-переходе документ остаётся тем же, и
 * сторонний скрипт втягивался бы прямо в авторизованный документ (§12). Сегодня эти маршруты
 * вошедшего не отсекают вовсе.
 *
 * Пока статус сессии `loading`, не делаем ничего: `AuthProvider` узнаёт про вкладку асинхронно, а
 * публичный маршрут монтируется сразу — уводить по неизвестному статусу значило бы выбрасывать
 * незалогиненного посетителя с формы. Виджет в это время всё равно не грузится: `useCaptcha`
 * держит состояние `loading`, а отправку блокируют формы (§5).
 */
export function useLeaveCaptchaPageIfAuthenticated(): boolean {
  const { status } = useAuth();

  useEffect(() => {
    if (status !== 'authenticated') return;
    window.location.assign('/');
  }, [status]);

  return status === 'authenticated';
}

/**
 * Блокируется ли отправка формы состоянием капчи (§5).
 *
 * `loading` — портал ещё не знает, требуется ли токен; `error` — не смог узнать. В обоих случаях
 * угадывать нельзя: отправив форму, портал либо получил бы отказ на ровном месте, либо оставил бы
 * ручку без проверки. `disabled` и `enabled` отправку не трогают: в первом случае токен пустой,
 * во втором его требует правило поля.
 */
export function captchaBlocksSubmit(captcha: CaptchaState): boolean {
  return captcha.status === 'loading' || captcha.status === 'error';
}

/**
 * Объяснение, почему кнопка отправки недоступна.
 *
 * Только для `loading`: при `error` сообщение и кнопку «Повторить» рисует само поле капчи, и
 * второй текст рядом с кнопкой лишь спорил бы с ним за внимание.
 */
export function CaptchaSubmitNote({ captcha }: { captcha: CaptchaState }) {
  if (captcha.status !== 'loading') return null;
  return (
    <Typography.Paragraph
      type="secondary"
      style={{ textAlign: 'center', marginTop: 8, marginBottom: 0 }}
    >
      Портал выясняет, нужна ли проверка, — отправка станет доступна через мгновение.
    </Typography.Paragraph>
  );
}
